/**
 * <responsive-video> — picks a desktop or (optional) mobile rendition at load time so
 * only ONE video ever downloads.
 *
 * Wraps a <video> whose <source>s are lazy (`data-video-source`, no `src`) and optionally
 * tagged `data-viewport` ('desktop' | 'mobile'), as emitted by video-sources.liquid when a
 * mobile rendition is set. We assign `src` for the current viewport only, then load().
 * When no mobile-tagged source exists (or sources are already eager), we no-op and the
 * <video>'s own sources play — the desktop video everywhere (progressive-enhancement fallback).
 */
class ResponsiveVideo extends HTMLElement {
  connectedCallback() {
    const video = this.querySelector('video');
    if (!(video instanceof HTMLVideoElement) || this.dataset.ready) return;

    const sources = [...video.querySelectorAll('source[data-video-source]')];
    if (!sources.length) return; // eager sources → nothing to pick
    this.dataset.ready = 'true';

    const isMobile = !window.matchMedia('(min-width: 750px)').matches;
    const hasMobile = sources.some((source) => source.dataset.viewport === 'mobile');
    const want = isMobile ? 'mobile' : 'desktop';

    for (const source of sources) {
      const useSource = hasMobile ? source.dataset.viewport === want : true;
      if (useSource && source.dataset.videoSource) {
        source.setAttribute('src', source.dataset.videoSource);
      }
    }

    video.load();
    if (video.autoplay) video.play?.().catch(() => {});
  }
}

if (!customElements.get('responsive-video')) {
  customElements.define('responsive-video', ResponsiveVideo);
}
